<template>
  <div class="distribution-workbench">
    <div class="d-flex flex-wrap align-center justify-space-between mb-3">
      <div>
        <div class="distribution-title">Distribuir atendimentos</div>
        <div class="distribution-subtitle">
          {{ linhas.length }} pedidos · {{ resumos.length }}
          {{ resumos.length === 1 ? "roteiro" : "roteiros" }}. Coleta e entrega
          permanecem juntas.
        </div>
      </div>
      <v-chip
        v-if="quantidadeAlteracoes"
        color="#b54708"
        text-color="#b54708"
        outlined
        >{{ quantidadeAlteracoes }}
        {{
          quantidadeAlteracoes === 1
            ? "transferência aguarda"
            : "transferências aguardam"
        }}
        validação</v-chip
      >
    </div>
    <div
      class="route-strip mb-4"
      role="group"
      aria-label="Filtrar pedidos por roteiro"
    >
      <button
        type="button"
        class="route-card"
        :class="{ active: !origem }"
        :aria-pressed="!origem"
        @click="filtrarRoteiro(null)"
      >
        <strong>Todos os roteiros</strong
        ><span>{{ linhas.length }} pedidos</span>
      </button>
      <button
        v-for="rota in resumos"
        :key="rota.placaVeiculo"
        type="button"
        class="route-card"
        :class="{ active: origem === rota.placaVeiculo }"
        :aria-pressed="origem === rota.placaVeiculo"
        @click="filtrarRoteiro(rota.placaVeiculo)"
      >
        <strong>Roteiro {{ rota.numeroRota }} · {{ rota.placaVeiculo }}</strong>
        <span
          >{{ contagens[rota.placaVeiculo] || 0 }} pedidos<span
            v-if="
              projetadas[rota.placaVeiculo] !== contagens[rota.placaVeiculo]
            "
          >
            → {{ projetadas[rota.placaVeiculo] || 0 }}</span
          ></span
        >
        <small
          >{{ Number(rota.totalKm || 0).toFixed(1) }} km ·
          {{ duracao(rota.totalHorasTotal || rota.totalHoras) }}
          confirmados</small
        >
      </button>
    </div>
    <div ref="operacoes" class="distribution-actions">
      <v-row dense align="center">
        <v-col cols="12" md="5">
          <v-text-field
            ref="busca"
            v-model="busca"
            label="Buscar pedido, cliente, origem ou destino"
            clearable
            class="
              input-style
              font-size-input
              text-light-input
              placeholder-light
              input-icon
            "
            dense
            flat
            filled
            solo
            hide-details
            @input="pagina = 1"
          >
            <template slot="prepend-inner">
              <v-icon color="#adb5bd" size=".875rem">fas fa-search</v-icon>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="origem"
            :items="opcoesOrigem"
            label="Roteiro de origem"
            clearable
            class="
              input-style
              font-size-input
              text-light-input
              placeholder-light
              input-icon
            "
            dense
            flat
            filled
            solo
            hide-details
            @change="mudarFiltro"
          />
        </v-col>
        <v-col cols="12" md="3" class="text-caption"
          >{{ filtradas.length }} encontrados ·
          {{ selecionados.length }} selecionados
          <span v-if="selecionadosForaFiltro" class="selection-warning">
            · {{ selecionadosForaFiltro }} fora do filtro</span
          >
          <v-btn
            v-if="!readonly"
            small
            text
            class="text-none"
            :disabled="busy || !filtradas.length"
            @click="selecionarFiltrados"
            >Selecionar {{ filtradas.length }} filtrados</v-btn
          >
        </v-col>
      </v-row>
      <v-row v-if="!readonly" dense align="center" class="mt-2">
        <v-col cols="12" md="5">
          <v-autocomplete
            ref="destino"
            v-model="destino"
            :items="opcoesDestino"
            label="Transferir selecionados para"
            :disabled="busy"
            class="
              input-style
              font-size-input
              text-light-input
              placeholder-light
              input-icon
            "
            dense
            flat
            filled
            solo
            hide-details
          />
        </v-col>
        <v-col cols="12" md="7" class="d-flex flex-wrap align-center">
          <v-btn
            height="39"
            :disabled="busy || !afetados.length"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-4
            "
            @click="preparar"
          >
            <v-icon left>mdi-arrow-right</v-icon>Preparar transferência
            <span v-if="afetados.length" class="ml-1"
              >({{ afetados.length }}
              {{ afetados.length === 1 ? "pedido" : "pedidos" }})</span
            >
          </v-btn>
          <v-btn
            text
            class="text-none"
            :disabled="busy || !selecionados.length"
            @click="selecionados = []"
            >Limpar seleção</v-btn
          >
        </v-col>
      </v-row>
      <div
        v-if="quantidadeAlteracoes && !readonly"
        class="pending-actions mt-3 pa-3"
      >
        <div class="text-body-2 mb-2">
          {{ quantidadeAlteracoes }} pedidos preparados. O mapa e os horários
          mostram a distribuição confirmada até a validação do motor.
        </div>
        <div class="d-flex flex-wrap">
          <v-btn
            height="39"
            :disabled="busy"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-4
            "
            @click="validar"
          >
            <v-icon left>mdi-check-decagram-outline</v-icon>Recalcular e validar
            {{ quantidadeAlteracoes }} transferências
          </v-btn>
          <v-btn
            height="39"
            :disabled="busy || !historico.length"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-ls btn-secondary
              bg-gradient-light
              px-4
            "
            @click="desfazer"
            >Desfazer última edição</v-btn
          >
          <v-btn text class="text-none" :disabled="busy" @click="descartar"
            >Descartar alterações</v-btn
          >
        </div>
      </div>
      <v-btn
        v-if="historico.length && !quantidadeAlteracoes && !readonly"
        text
        class="text-none"
        :disabled="busy"
        @click="desfazer"
        >Desfazer descarte / última edição</v-btn
      >
      <v-alert
        v-if="aviso"
        dense
        text
        color="#b54708"
        icon="mdi-alert-outline"
        class="mt-3 mb-0"
        >{{ aviso }}</v-alert
      >
      <v-alert
        v-if="mensagem"
        dense
        text
        color="#027a48"
        icon="mdi-check-circle-outline"
        class="mt-3 mb-0"
        dismissible
        >{{ mensagem }}</v-alert
      >
    </div>
    <v-data-table
      v-model="selecionados"
      :headers="headers"
      :items="filtradas"
      item-key="pedido"
      :show-select="!readonly"
      :page.sync="pagina"
      :items-per-page="25"
      :footer-props="{
        'items-per-page-options': [25, 50, 100],
        'items-per-page-text': 'Pedidos por página:',
      }"
      class="mt-3 crud-listagem-table distribution-table"
      dense
      no-data-text="Nenhum pedido corresponde aos filtros."
    >
      <template #header.data-table-select="{ props, on }">
        <input
          type="checkbox"
          class="routing-checkbox"
          :checked="props.value"
          :indeterminate.prop="props.indeterminate"
          :disabled="busy || readonly || !filtradas.length"
          aria-label="Selecionar pedidos desta página"
          @change="on.input($event.target.checked)"
        />
      </template>
      <template #item.data-table-select="{ item, isSelected, select }">
        <input
          type="checkbox"
          class="routing-checkbox"
          :checked="isSelected"
          :disabled="busy || readonly"
          :aria-label="`Selecionar pedido ${item.pedido}`"
          @click.stop
          @change="select($event.target.checked)"
        />
      </template>
      <template #item.pedido="{ item }">
        <strong>{{ item.pedido }}</strong>
        <div class="client-name" :title="item.cliente">{{ item.cliente }}</div>
      </template>
      <template #item.origem="{ item }"
        ><div class="local-name" :title="item.descricaoOrigem">
          {{ item.descricaoOrigem }}
        </div>
        <small>{{ item.horaOrigem }}</small></template
      >
      <template #item.destino="{ item }"
        ><div class="local-name" :title="item.descricaoDestino">
          {{ item.descricaoDestino }}
        </div>
        <small>{{ item.horaDestino }}</small></template
      >
      <template #item.placaVeiculo="{ item }">
        <div>{{ nomeRoteiro(item.placaVeiculo) }}</div>
        <v-chip
          v-if="alteracoes[item.pedido]"
          small
          outlined
          color="#b54708"
          text-color="#b54708"
          class="my-1"
          >Preparado → {{ nomeRoteiro(alteracoes[item.pedido]) }}</v-chip
        >
      </template>
      <template #item.valor="{ item }">{{ moeda(item.valor) }}</template>
      <template #item.acao="{ item }"
        ><v-btn
          v-if="!readonly"
          small
          text
          class="text-none"
          :disabled="busy"
          :aria-label="`Mover o pedido ${item.pedido} completo`"
          @click="moverUm(item)"
          ><v-icon small left>mdi-arrow-right</v-icon>Mover</v-btn
        ></template
      >
    </v-data-table>
  </div>
</template>

<script>
function normalizar(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}
export default {
  name: "DistribuicaoPedidos",
  props: {
    plano: { type: Object, required: true },
    busy: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      busca: "",
      origem: null,
      destino: null,
      selecionados: [],
      pagina: 1,
      alteracoes: {},
      historico: [],
      mensagem: "",
      aviso: "",
      submetidas: 0,
      headers: [
        { text: "Pedido", value: "pedido", width: 90 },
        {
          text: "Coleta ou assistência / início",
          value: "origem",
          sortable: false,
        },
        {
          text: "Entrega ou conclusão / início",
          value: "destino",
          sortable: false,
        },
        { text: "Roteiro / veículo", value: "placaVeiculo" },
        { text: "Valor", value: "valor", align: "end" },
        { text: "", value: "acao", sortable: false, width: 90 },
      ],
    };
  },
  computed: {
    storageKey() {
      return `roteirizacao-transferencias:${this.plano.id}`;
    },
    resumos() {
      return this.plano.resultado?.resumoRotas || [];
    },
    porPedido() {
      const result = {};
      for (const stop of this.plano.resultado?.paradas || []) {
        if (!stop.pedido) continue;
        const key = ["COLETA", "ASSISTENCIA"].includes(stop.tipo)
          ? "origem"
          : "destino";
        (result[stop.pedido] || (result[stop.pedido] = {}))[key] = this.horario(
          stop.inicioAtendimento
        );
      }
      return result;
    },
    linhas() {
      return (this.plano.resultado?.rotas || []).map((row) => ({
        ...row,
        isSelectable: !this.busy && !this.readonly,
        horaOrigem: this.porPedido[row.pedido]?.origem,
        horaDestino: this.porPedido[row.pedido]?.destino,
        pesquisa: normalizar(
          `${row.pedido} ${row.cliente || ""} ${row.descricaoOrigem} ${
            row.descricaoDestino
          } ${row.placaVeiculo}`
        ),
      }));
    },
    filtradas() {
      const search = normalizar(this.busca);
      return this.linhas.filter(
        (row) =>
          (!this.origem || row.placaVeiculo === this.origem) &&
          (!search || row.pesquisa.includes(search))
      );
    },
    contagens() {
      return this.linhas.reduce((acc, row) => {
        acc[row.placaVeiculo] = (acc[row.placaVeiculo] || 0) + 1;
        return acc;
      }, {});
    },
    projetadas() {
      const counts = { ...this.contagens };
      for (const row of this.linhas) {
        const target = this.alteracoes[row.pedido];
        if (target) {
          counts[row.placaVeiculo]--;
          counts[target] = (counts[target] || 0) + 1;
        }
      }
      return counts;
    },
    opcoesOrigem() {
      return this.resumos.map((r) => ({
        value: r.placaVeiculo,
        text: `Roteiro ${r.numeroRota} · ${r.placaVeiculo}`,
      }));
    },
    opcoesDestino() {
      const order = new Map(
        this.resumos.map((r) => [r.placaVeiculo, r.numeroRota])
      );
      return [...(this.plano.veiculos || [])]
        .sort(
          (a, b) =>
            (order.get(a.placa) || Infinity) -
              (order.get(b.placa) || Infinity) || a.placa.localeCompare(b.placa)
        )
        .map((v) => ({
          value: v.placa,
          text: `${this.nomeRoteiro(v.placa)} · ${
            this.projetadas[v.placa] || 0
          } pedidos`,
        }));
    },
    quantidadeAlteracoes() {
      return Object.keys(this.alteracoes).length;
    },
    selecionadosForaFiltro() {
      const visible = new Set(this.filtradas.map((row) => row.pedido));
      return this.selecionados.filter((row) => !visible.has(row.pedido)).length;
    },
    afetados() {
      return this.destino
        ? this.selecionados.filter(
            (row) =>
              (this.alteracoes[row.pedido] || row.placaVeiculo) !== this.destino
          )
        : [];
    },
  },
  watch: {
    readonly(value) {
      if (value && this.quantidadeAlteracoes) {
        this.aviso =
          "Este planejamento já foi aprovado. As transferências locais pendentes não foram aplicadas.";
        this.alteracoes = {};
        this.historico = [];
        this.submetidas = 0;
      }
    },
    alteracoes: {
      deep: true,
      handler() {
        this.$emit("pendentes", this.quantidadeAlteracoes);
        this.persistir();
      },
    },
    "plano.revisao"(value, previous) {
      if (value === previous) return;
      const current = new Map(
        this.linhas.map((row) => [String(row.pedido), row.placaVeiculo])
      );
      const applied = Object.entries(this.alteracoes).filter(
        ([id, plate]) => current.get(id) === plate
      ).length;
      const remaining = this.reconciliar(this.alteracoes);
      this.alteracoes = remaining;
      this.historico = [];
      this.selecionados = [];
      this.submetidas = 0;
      this.mensagem = applied
        ? `${applied} ${
            applied === 1
              ? "pedido no destino confirmado"
              : "pedidos nos destinos confirmados"
          }. Horários revalidados.`
        : "A distribuição foi atualizada.";
      this.aviso = Object.keys(remaining).length
        ? "O planejamento mudou. As transferências ainda não aplicadas foram mantidas para nova validação."
        : "";
      if (
        this.origem &&
        !this.resumos.some((r) => r.placaVeiculo === this.origem)
      )
        this.origem = null;
    },
  },
  mounted() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || "null");
      if (saved && !this.readonly) {
        this.alteracoes = this.reconciliar(saved.alteracoes || {});
        if (saved.revisao !== this.plano.revisao && this.quantidadeAlteracoes)
          this.aviso =
            "Rascunho recuperado sobre uma revisão nova. Confira e valide as transferências restantes.";
      }
      if (!this.quantidadeAlteracoes) this.persistir();
    } catch (error) {
      this.aviso =
        "Não foi possível recuperar o rascunho local. Mantenha esta tela aberta durante as edições.";
    }
    this.$emit("pendentes", this.quantidadeAlteracoes);
  },
  methods: {
    nomeRoteiro(plate) {
      const r = this.resumos.find((row) => row.placaVeiculo === plate);
      return r
        ? `Roteiro ${r.numeroRota} · ${plate}`
        : `Novo roteiro · ${plate}`;
    },
    filtrarRoteiro(plate) {
      this.origem = plate;
      this.mudarFiltro();
    },
    mudarFiltro() {
      this.pagina = 1;
    },
    preparar() {
      if (this.busy || this.readonly || !this.afetados.length) return;
      this.historico.push({ ...this.alteracoes });
      if (this.historico.length > 20) this.historico.shift();
      const next = { ...this.alteracoes };
      for (const row of this.afetados) {
        if (this.destino === row.placaVeiculo) delete next[row.pedido];
        else next[row.pedido] = this.destino;
      }
      this.alteracoes = next;
      this.selecionados = [];
      this.mensagem = "";
    },
    desfazer() {
      if (this.busy || !this.historico.length) return;
      this.alteracoes = this.historico.pop();
      this.mensagem = "";
    },
    descartar() {
      if (this.busy) return;
      this.historico.push({ ...this.alteracoes });
      if (this.historico.length > 20) this.historico.shift();
      this.alteracoes = {};
      this.submetidas = 0;
      this.mensagem = "Alterações descartadas. Você pode desfazer esta ação.";
    },
    validar() {
      if (this.busy || this.readonly || !this.quantidadeAlteracoes) return;
      this.submetidas = this.quantidadeAlteracoes;
      this.$emit(
        "transferir",
        Object.entries(this.alteracoes).map(([pedido, placaDestino]) => ({
          pedido: Number(pedido),
          placaDestino,
        }))
      );
    },
    moverUm(row) {
      this.selecionados = [row];
      this.destino = null;
      this.$refs.operacoes.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      this.$nextTick(() => this.$refs.destino.focus());
    },
    persistir() {
      try {
        if (this.quantidadeAlteracoes)
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({
              revisao: this.plano.revisao,
              alteracoes: this.alteracoes,
            })
          );
        else localStorage.removeItem(this.storageKey);
      } catch (error) {
        this.aviso =
          "Não foi possível guardar o rascunho neste navegador. Mantenha a tela aberta até validar ou descartar as alterações.";
      }
    },
    reconciliar(changes) {
      const known = new Map(
        this.linhas.map((row) => [String(row.pedido), row.placaVeiculo])
      );
      const vehicles = new Set((this.plano.veiculos || []).map((v) => v.placa));
      return Object.fromEntries(
        Object.entries(changes).filter(
          ([id, plate]) =>
            known.has(id) && vehicles.has(plate) && known.get(id) !== plate
        )
      );
    },
    selecionarFiltrados() {
      if (this.busy || this.readonly) return;
      const ids = new Set(this.selecionados.map((row) => row.pedido));
      this.selecionados = [
        ...this.selecionados,
        ...this.filtradas.filter((row) => !ids.has(row.pedido)),
      ];
    },
    horario(value) {
      if (!value) return "";
      const [date, time] = value.split("T");
      return `${date.split("-").reverse().join("/")} ${time.substring(0, 5)}`;
    },
    duracao(hours) {
      const minutes = Math.round(Number(hours || 0) * 60);
      return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(
        2,
        "0"
      )}`;
    },
    moeda(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },
};
</script>

<style scoped>
.distribution-title {
  color: #202538;
  font-size: 1rem;
  font-weight: 800;
}
.distribution-subtitle {
  color: #667085;
  font-size: 0.78rem;
}
.route-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 2px 10px;
}
.route-card {
  flex: 0 0 auto;
  min-width: 175px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  border: 1px solid #e6e9ef;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(33, 43, 54, 0.04);
  color: #344767;
  background: #fff;
  text-align: left;
  transition: border-color 0.18s ease, background-color 0.18s ease;
}
.route-card:hover {
  border-color: #cdd5f8;
}
.route-card.active {
  border: 2px solid #5e72e4;
  background: #f5f7ff;
  padding: 11px 15px;
}
.route-card:focus-visible {
  outline: 3px solid #6e91ff;
  outline-offset: 2px;
}
.route-card small {
  color: #5a6475;
}
.distribution-actions {
  position: sticky;
  top: 64px;
  z-index: 3;
  background: white;
  padding: 12px 0;
  border-bottom: 1px solid #edf0f4;
}
.pending-actions {
  background: #fff8eb;
  border: 1px solid #efce94;
  border-radius: 8px;
  color: #4d3a19;
}
.local-name {
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style scoped>
.client-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  color: #5a6475;
}
</style>

<style scoped>
.routing-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #5e72e4;
  cursor: pointer;
  vertical-align: middle;
}
.routing-checkbox:focus-visible {
  outline: 3px solid #8da8ff;
  outline-offset: 2px;
}
</style>

<style scoped>
.selection-warning {
  color: #8a4b00;
  font-weight: 600;
}
@media (max-height: 760px), (max-width: 960px) {
  .distribution-actions {
    position: static;
  }
}
</style>
